import { useMemo } from 'react';
/**
 * Hook to calculate dynamic font size based on character count
 * Rules:
 * - More than 30 characters: reduce by 25%
 * - More than 50 characters: reduce by 30%
 * - More than 70 characters: reduce by 50%
 *
 * @param text - The text content to analyze
 * @param defaultVariant - The default typography variant to use
 * @returns The appropriate typography variant based on character count
 */
export function useDynamicFontSize(text, defaultVariant = 'heading-3xl') {
    return useMemo(() => {
        return getDynamicFontVariant(text, defaultVariant);
    }, [text, defaultVariant]);
}
/**
 * Get character count from text content
 * @param text - The text content to count
 * @returns Number of characters
 */
export function getCharacterCount(text) {
    const textString = typeof text === 'string' ? text : String(text);
    return textString.length;
}
/**
 * Get dynamic font variant based on character count (non-hook version)
 * @param text - The text content to analyze
 * @param defaultVariant - The default typography variant to use
 * @returns The appropriate typography variant based on character count
 */
export function getDynamicFontVariant(text, defaultVariant = 'heading-3xl') {
    const textString = typeof text === 'string' ? text : String(text);
    const charCount = textString.length;
    // For heading-4xl default
    if (defaultVariant === 'heading-4xl') {
        if (charCount > 70)
            return 'heading-xl';
        if (charCount > 50)
            return 'heading-2xl';
        if (charCount > 33)
            return 'heading-3xl';
        return defaultVariant;
    }
    // For heading-3xl default
    if (defaultVariant === 'heading-3xl') {
        if (charCount > 70)
            return 'heading-xl';
        if (charCount > 33)
            return 'heading-2xl';
        return defaultVariant;
    }
    return defaultVariant;
}
/**
 * Helper function to get dynamic font class string with 'type-' prefix
 * @param text - The text content to analyze
 * @param defaultVariant - The default typography variant to use
 * @returns The CSS class string with 'type-' prefix
 */
export function getDynamicFontClass(text, defaultVariant) {
    const variant = getDynamicFontVariant(text, defaultVariant);
    return `type-${variant}`;
}
